import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useT } from '../i18n';
import { useSession } from '../context/Session';
import { Empty, Icon, Loading, Spinner, useConfirm, useToast } from '../components/ui';
import { adminFeedback } from '../lib/cloud';

// Admin panel: feedback sent from the app (FeedbackService) and the website
// (Settings). Read and triaged through the admin_feedback_api function only.

const TYPES = [
  ['bug', 'feedbackTypeBug', 'bug_report'],
  ['feature', 'feedbackTypeFeature', 'lightbulb'],
  ['ux', 'feedbackTypeUx', 'sentiment_dissatisfied'],
  ['general', 'feedbackTypeGeneral', 'chat'],
];
const STATUSES = [['new', 'adminFbStatusNew'], ['read', 'adminFbStatusRead'], ['done', 'adminFbStatusDone']];

function typeInfo(type) {
  return TYPES.find(([v]) => v === type) || TYPES[3];
}

function FeedbackCard({ item, onStatus, onDelete }) {
  const t = useT();
  const toast = useToast();
  const [busy, setBusy] = useState(false);
  const [, labelKey, icon] = typeInfo(item.type);
  const date = item.created_at
    ? new Date(item.created_at).toLocaleString(t.locale, { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })
    : '—';
  const device = [item.platform, item.app_version !== 'web' && item.app_version, item.device_model !== 'browser' && item.device_model]
    .filter((v) => v && v !== 'unknown')
    .join(' · ');
  const os = item.platform === 'web' ? '' : item.os_version && item.os_version !== 'unknown' ? item.os_version : '';

  const setStatus = async (status) => {
    setBusy(true);
    await onStatus(item, status);
    setBusy(false);
  };
  const copy = async (text) => {
    try {
      await navigator.clipboard.writeText(text);
      toast.success(t('adminCopied'));
    } catch (e) {
      // clipboard blocked; nothing to do
    }
  };

  return (
    <article className={`gm-card gm-card--pad-lg gm-admin-fb${item.status === 'done' ? ' gm-admin-fb--done' : ''}`}>
      <div className="gm-admin-fb__head">
        <span className={`gm-admin-fb__type gm-admin-fb__type--${item.type}`}>
          <Icon name={icon} size={16} /> {t(labelKey)}
        </span>
        {item.status === 'new' && <span className="gm-admin-fb__new">{t('adminFbStatusNew')}</span>}
        <span className="gm-small gm-muted" style={{ marginLeft: 'auto' }}>{date}</span>
      </div>
      <p className="gm-admin-fb__msg">{item.message}</p>
      <div className="gm-admin-fb__foot">
        <div className="gm-small" style={{ minWidth: 0 }}>
          {item.user_id ? (
            <button type="button" className="gm-link" onClick={() => copy(item.email || item.user_id)} title={item.user_id}>
              {item.name || item.email || item.user_id}
            </button>
          ) : (
            <span className="gm-muted">{t('adminFbAnonymous')}</span>
          )}
          {item.name && item.email && <span className="gm-muted"> · {item.email}</span>}
          {(device || os) && <div className="gm-muted">{[device, os && `OS ${os}`].filter(Boolean).join(' · ')}</div>}
        </div>
        <div className="gm-row" style={{ gap: 8, flexShrink: 0 }}>
          {busy ? <Spinner /> : (
            <select
              className="gm-select gm-admin-fb__status"
              value={item.status}
              onChange={(e) => setStatus(e.target.value)}
              aria-label={t('adminFbStatus')}
            >
              {STATUSES.map(([v, k]) => <option key={v} value={v}>{t(k)}</option>)}
            </select>
          )}
          <button
            type="button"
            className="gm-icon-btn gm-icon-btn--sm"
            style={{ color: 'var(--gm-error)' }}
            onClick={() => onDelete(item)}
            disabled={busy}
            title={t('adminDelete')}
            aria-label={t('adminDelete')}
          >
            <Icon name="delete" size={20} />
          </button>
        </div>
      </div>
    </article>
  );
}

export default function AdminFeedback({ onNewCount }) {
  const t = useT();
  const toast = useToast();
  const confirm = useConfirm();
  const { refreshAdmin } = useSession();
  // Loads only on Refresh (also the first time): undefined = not loaded yet, null = loading.
  const [items, setItems] = useState(undefined);
  const [error, setError] = useState('');
  const [status, setStatusFilter] = useState('open');
  const [type, setType] = useState('all');

  const load = useCallback(async () => {
    setItems(null);
    setError('');
    try {
      let res = await adminFeedback('list');
      if (res.error === 'forbidden') {
        // Claim may have been granted after this session's token was minted.
        if (await refreshAdmin(true)) res = await adminFeedback('list');
      }
      if (res.feedback) setItems(res.feedback);
      else {
        setItems([]);
        setError(res.error === 'forbidden' ? t('anErrorOccurred') : String(res.error || ''));
      }
    } catch (e) {
      setItems([]);
      setError(t('anErrorOccurred'));
    }
  }, [refreshAdmin, t]);

  const newCount = items ? items.filter((i) => i.status === 'new').length : 0;
  useEffect(() => { if (items) onNewCount?.(newCount); }, [items, newCount, onNewCount]);

  const shown = useMemo(() => (items || []).filter((i) => {
    if (type !== 'all' && i.type !== type) return false;
    if (status === 'open') return i.status !== 'done';
    return status === 'all' || i.status === status;
  }), [items, status, type]);

  const changeStatus = async (item, next) => {
    const res = await adminFeedback('setStatus', { id: item.id, status: next });
    if (res.ok) setItems((list) => list.map((i) => (i.id === item.id ? { ...i, status: next } : i)));
    else toast.error(t('anErrorOccurred'));
  };

  const remove = async (item) => {
    const ok = await confirm({
      title: t('adminFbDeleteTitle'),
      message: item.message.length > 140 ? `${item.message.slice(0, 140)}…` : item.message,
      confirmLabel: t('adminDelete'),
      danger: true,
    });
    if (!ok) return;
    const res = await adminFeedback('delete', { id: item.id });
    if (res.ok) {
      setItems((list) => list.filter((i) => i.id !== item.id));
      toast.success(t('adminFbDeleted'));
    } else {
      toast.error(t('anErrorOccurred'));
    }
  };

  const markAllRead = async () => {
    const fresh = items.filter((i) => i.status === 'new');
    await Promise.all(fresh.map((i) => changeStatus(i, 'read')));
  };

  const statusFilters = [['open', 'adminFbOpen'], ...STATUSES, ['all', 'adminFbAll']];

  return (
    <div className="gm-stack">
      <div className="gm-row" style={{ justifyContent: 'space-between', flexWrap: 'wrap', gap: 8 }}>
        <div className="gm-chips">
          {statusFilters.map(([v, k]) => (
            <button key={v} type="button" className={`gm-chip${status === v ? ' selected' : ''}`} onClick={() => setStatusFilter(v)}>
              {t(k)}{v === 'new' && newCount > 0 ? ` (${newCount})` : ''}
            </button>
          ))}
        </div>
        <div className="gm-row" style={{ gap: 8 }}>
          {newCount > 0 && (
            <button type="button" className="gm-btn gm-btn--soft gm-btn--sm" onClick={markAllRead}>
              <Icon name="done_all" size={18} /> {t('adminFbMarkAllRead')}
            </button>
          )}
          <button type="button" className="gm-btn gm-btn--soft gm-btn--sm" onClick={load}>
            <Icon name="refresh" size={18} /> {t('adminRefresh')}
          </button>
        </div>
      </div>
      <div className="gm-chips">
        <button type="button" className={`gm-chip${type === 'all' ? ' selected' : ''}`} onClick={() => setType('all')}>{t('adminFbAllTypes')}</button>
        {TYPES.map(([v, k, icon]) => (
          <button key={v} type="button" className={`gm-chip${type === v ? ' selected' : ''}`} onClick={() => setType(v)}>
            <Icon name={icon} size={18} /> {t(k)}
          </button>
        ))}
      </div>
      {items === undefined && <section className="gm-card gm-card--pad-lg"><Empty icon="refresh">{t('adminPressRefresh')}</Empty></section>}
      {items === null && <Loading />}
      {error && <p className="gm-error-text gm-small">{error}</p>}
      {items && !error && shown.length === 0 && (
        <section className="gm-card gm-card--pad-lg"><Empty icon="forum">{t('adminFbEmpty')}</Empty></section>
      )}
      {shown.map((i) => <FeedbackCard key={i.id} item={i} onStatus={changeStatus} onDelete={remove} />)}
    </div>
  );
}
